import { Command } from 'commander';
import { ApiClient } from '../client.js';
import { formatOutput } from '../output.js';
import { getBase, getFormat, parseData } from '../helpers.js';
import { confirm } from '../prompt.js';

export const actionsCommand = new Command('actions')
  .description('Manage Actions — reusable, named definitions: one record_type + one operation, or a composite of atomic write steps (--config with `steps`)');

actionsCommand.command('upsert <id>')
  .description('Create or update an Action — one record_type + one operation, or a composite (--config with `steps`)')
  .option('--record_type <record_type>', 'The record_type this action operates over (single-op; omit for a composite)')
  .option('--operation <op>', 'The operation: create | get | list | update | delete (single-op; omit for a composite)')
  .option('--description <desc>', 'Agent-facing description')
  .option('--config <json>', 'Full Action config as JSON. Single-op: writable_fields, filterable_fields, precondition, binding, external_source, expose_*/default_*, agent_minimal. Composite: `steps` — an ordered list of atomic write steps ({key, record_type|rel_type, operation, writable_fields?, precondition?}) run in one transaction. Use @file.json to read from file.')
  .action(async function (this: Command, id: string, opts: {
    record_type?: string;
    operation?: string;
    description?: string;
    config?: string;
  }) {
    const ws = getBase(this);
    const client = new ApiClient();

    let body: Record<string, unknown>;
    if (opts.config) {
      body = parseData(opts.config);
      if (opts.record_type) body.record_type = opts.record_type;
      if (opts.operation) body.operation = opts.operation;
      if (opts.description) body.description = opts.description;
    } else {
      if (!opts.record_type || !opts.operation) {
        console.error('Provide --record_type and --operation (or --config <json>).');
        process.exit(1);
      }
      body = { record_type: opts.record_type, operation: opts.operation };
      if (opts.description) body.description = opts.description;
    }

    const data = await client.request('PUT', `/v1/${ws}/actions/${id}`, body);
    console.log(formatOutput(data, getFormat(this)));
  });

actionsCommand.command('get <id>')
  .description('Get an Action by id')
  .action(async function (this: Command, id: string) {
    const ws = getBase(this);
    const client = new ApiClient();
    const data = await client.request('GET', `/v1/${ws}/actions/${id}`);
    console.log(formatOutput(data, getFormat(this)));
  });

actionsCommand.command('list')
  .description('List Actions in this database')
  .action(async function (this: Command) {
    const ws = getBase(this);
    const client = new ApiClient();
    const data = await client.request('GET', `/v1/${ws}/actions`);
    console.log(formatOutput(data, getFormat(this)));
  });

actionsCommand.command('delete <id>')
  .description('Delete an Action')
  .option('-y, --yes', 'Skip confirmation')
  .action(async function (this: Command, id: string, opts: { yes?: boolean }) {
    if (!opts.yes && !(await confirm(`Delete action ${id}? This cannot be undone.`))) {
      console.log('Aborted.');
      return;
    }
    const ws = getBase(this);
    const client = new ApiClient();
    await client.request('DELETE', `/v1/${ws}/actions/${id}`);
    console.log(`Deleted action ${id}`);
  });
